import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  Tooltip,
  Typography,
  Grid,
  Tab,
} from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import Checkbox from "@mui/material/Checkbox";
import RemoveRedEyeRoundedIcon from "@mui/icons-material/RemoveRedEyeRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenRounded";
import { AnimatePresence, motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { getCases } from "../../../redux/Apps/Lawyerapp/CaseSlice/CaseSlice";
import { getAllTask } from "../../../redux/Apps/Lawyerapp/TaskSlice/TaskSlice";
import { getAllBilles } from "../../../redux/Apps/Lawyerapp/BillingSlice/BillingSlice";
import { getActivities } from "../../../redux/Apps/Lawyerapp/ActivitySlice/ActivitySlice";
import { getAllFilterContacts } from "../../../redux/Apps/Lawyerapp/ContactSlice/ContactSlice";
import { getAllUsers } from "../../../redux/Apps/users/userSlice";
import { backend_api } from "../../../static/server";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  Legend,
  Text,
} from "recharts";
import axios from "axios";
import { NoDataOptionField } from "../../../static/staticdata";
import CreateTaskForm from "../../../pages/LawyerPages/Task/TaskModuleForms/CreateTaskForm";
import noOption from "../../../assets/no-data-found.webp";
import { useDialog } from "../../DialogContext";

// Primary color constant
const PRIMARY_COLOR = "#00B3B0";
const PRIMARY_DARK = "#008c8a";

const CircularProgress = ({ percentage, completed, target, period }) => {
  const radius = 60;
  const stroke = 8;
  const normalizedRadius = radius - stroke / 2;
  const circumference = 2 * Math.PI * normalizedRadius;
  const progress = (percentage / 100) * circumference;

  // Handle NaN values
  const safePercentage = isNaN(percentage)
    ? 0
    : Math.min(100, Math.max(0, percentage));
  const safeCompleted = isNaN(completed) ? 0 : completed.toFixed(1);
  const safeTarget = isNaN(target) ? 0 : target.toFixed(1);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
      }}
    >
      <Box sx={{ position: "relative", display: "inline-flex" }}>
        <svg
          width={2 * radius + stroke}
          height={2 * radius + stroke}
          style={{ transform: "rotate(-90deg)" }}
        >
          <circle
            cx={radius + stroke / 2}
            cy={radius + stroke / 2}
            r={normalizedRadius}
            stroke="#e0e0e0"
            strokeWidth={stroke}
            fill="none"
          />
          <circle
            cx={radius + stroke / 2}
            cy={radius + stroke / 2}
            r={normalizedRadius}
            stroke={PRIMARY_COLOR}
            strokeWidth={stroke}
            fill="none"
            strokeDasharray={`${progress} ${circumference}`}
            strokeLinecap="round"
            style={{
              transition: "stroke-dasharray 0.5s ease-in-out",
              filter: "drop-shadow(0px 3px 6px rgba(0, 179, 176, 0.3))",
            }}
          />
        </svg>

        {/* Centered Label */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {safePercentage}%
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {safeCompleted}h / {safeTarget}h
          </Typography>
        </Box>
      </Box>

      <Typography variant="body2" sx={{ textAlign: "center" }}>
        {period} Progress
      </Typography>
    </Box>
  );
};

const PersonalDash = () => {
  const [value, setValue] = useState(1);
  const [graphFullScreen, setGraphFullScreen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { showDialog } = useDialog();

  const dispatch = useDispatch();
  const {
    token,
    teamMemberLawyerId,
    lawerId,
    userId,
    allUsers,
    userPagination,
  } = useSelector((state) => state.user);
  const { allCase, casePagination } = useSelector((state) => state.case);
  const { allTask } = useSelector((state) => state.task);
  const { allBilles } = useSelector((state) => state.billing);
  const { allActivities } = useSelector((state) => state.activity);
  const { filteredContacts, filteredContactsPagenation } = useSelector(
    (state) => state.contact
  );
  const [pendingMatterCount, setPendingMatterCount] = useState(0);

  useEffect(() => {
    if (graphFullScreen) {
      setIsLoading(true);
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [graphFullScreen]);

  useEffect(() => {
    const fetchPendingMatter = async () => {
      try {
        const res = await axios.get(
          `${backend_api}case/register/?status=pending`,
          {
            headers: {
              Authorization: `Token ${token}`,
            },
          }
        );
        setPendingMatterCount(res.data.count);
      } catch (err) {
        console.error("Error fetching pending matter:", err);
        setPendingMatterCount(0);
      }
    };

    fetchPendingMatter();
  }, [token]);

  const userCases = useMemo(() => {
    if (!Array.isArray(allCase)) return [];
    return allCase.filter(
      (c) =>
        c?.created_by?.id === userId ||
        c?.lawyer?.id === lawerId ||
        (Array.isArray(c?.case_lawyers) &&
          c.case_lawyers.some((l) => l.id === lawerId))
    );
  }, [allCase, userId, lawerId]);

  const userTasks = useMemo(() => {
    if (!Array.isArray(allTask)) return [];
    return allTask.filter(
      (t) => t?.assign_to?.id === userId || t?.created_by?.id === userId
    );
  }, [allTask, userId]);

  const userBills = useMemo(() => {
    if (!Array.isArray(allBilles)) return [];
    const caseIds = new Set(userCases.map((c) => c.id));
    return allBilles.filter(
      (b) =>
        caseIds.has(b?.case?.id) ||
        b?.lawyer?.id === lawerId ||
        b?.created_by?.id === userId
    );
  }, [allBilles, userCases, lawerId, userId]);

  const userActivities = useMemo(() => {
    if (!Array.isArray(allActivities)) return [];
    return allActivities.filter(
      (a) => a?.lawyer?.id === lawerId || a?.created_by?.id === userId
    );
  }, [allActivities, lawerId, userId]);

  const tasksToday = useMemo(() => {
    const today = new Date().toDateString();
    return userTasks.filter((t) => {
      const due = t?.closed_on || t?.due_date;
      return due && new Date(due).toDateString() === today;
    });
  }, [userTasks]);

  const monthlyProgressData = useMemo(() => {
    const months = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = date.toLocaleString("en-US", { month: "short" });

      const revenue = userBills
        .filter((b) => {
          const d = new Date(b.created_at || b.date);
          return (
            b.status === "paid" &&
            d.getMonth() === date.getMonth() &&
            d.getFullYear() === date.getFullYear()
          );
        })
        .reduce((sum, b) => sum + Number(b.total_amount || 0), 0);

      const billable = userActivities
        .filter((a) => {
          if (a.type !== "time" || !a.time_in || !a.time_out || a.non_billable)
            return false;
          const d = new Date(a.time_in);
          return (
            d.getMonth() === date.getMonth() &&
            d.getFullYear() === date.getFullYear()
          );
        })
        .reduce(
          (sum, a) =>
            sum + (new Date(a.time_out) - new Date(a.time_in)) / 3600000,
          0
        );

      months.push({
        month: monthName,
        billable: parseFloat(billable.toFixed(2)),
        target: 50,
        revenue,
      });
    }
    return months;
  }, [userBills, userActivities]);

  const totalBillableHours = useMemo(() => {
    return userActivities
      .filter((a) => a.type === "time" && a.time_in && a.time_out)
      .reduce(
        (sum, a) =>
          sum + (new Date(a.time_out) - new Date(a.time_in)) / 3600000,
        0
      );
  }, [userActivities]);

  const billableHours = useMemo(() => {
    return userActivities
      .filter(
        (a) => a.type === "time" && a.time_in && a.time_out && !a.non_billable
      )
      .reduce(
        (sum, a) =>
          sum + (new Date(a.time_out) - new Date(a.time_in)) / 3600000,
        0
      );
  }, [userActivities]);

  const nonBillableHours = useMemo(() => {
    return userActivities
      .filter(
        (a) => a.type === "time" && a.time_in && a.time_out && a.non_billable
      )
      .reduce(
        (sum, a) =>
          sum + (new Date(a.time_out) - new Date(a.time_in)) / 3600000,
        0
      );
  }, [userActivities]);

  const pieChartData = useMemo(() => {
    const admin = Math.max(
      totalBillableHours - billableHours - nonBillableHours,
      0
    );
    return [
      { name: "Billable Hours", value: billableHours, color: PRIMARY_COLOR },
      { name: "Non-billable", value: nonBillableHours, color: "#ff9800" },
      { name: "Admin", value: admin, color: "#4caf50" },
    ];
  }, [billableHours, nonBillableHours, totalBillableHours]);

  const hourlyMetricsData = useMemo(() => {
    const compute = (start) =>
      userActivities.reduce((sum, a) => {
        if (
          a.type === "time" &&
          a.time_in &&
          a.time_out &&
          !a.non_billable &&
          new Date(a.time_in) >= start
        ) {
          return sum + (new Date(a.time_out) - new Date(a.time_in)) / 3600000;
        }
        return sum;
      }, 0);

    const today = new Date();
    const weekStart = new Date();
    weekStart.setDate(today.getDate() - today.getDay());
    weekStart.setHours(0, 0, 0, 0);
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const yearStart = new Date(today.getFullYear(), 0, 1);

    const todayHours = compute(new Date(today.setHours(0, 0, 0, 0)));
    const weekHours = compute(weekStart);
    const monthHours = compute(monthStart);
    const yearHours = compute(yearStart);

    return {
      today: {
        completed: todayHours,
        target: 2,
        percentage: (todayHours / 2) * 100,
      },
      week: {
        completed: weekHours,
        target: 9.6,
        percentage: (weekHours / 9.6) * 100,
      },
      month: {
        completed: monthHours,
        target: 41.7,
        percentage: (monthHours / 41.7) * 100,
      },
      year: {
        completed: yearHours,
        target: 500,
        percentage: (yearHours / 500) * 100,
      },
    };
  }, [userActivities]);

  const hourlyTabs = useMemo(
    () => [
      { period: "Today", value: 1, data: hourlyMetricsData.today },
      { period: "This Week", value: 2, data: hourlyMetricsData.week },
      { period: "This Month", value: 3, data: hourlyMetricsData.month },
      { period: "This Year", value: 4, data: hourlyMetricsData.year },
    ],
    [hourlyMetricsData]
  );

  const personalDashBillData = useMemo(() => {
    const draft = userBills.filter((b) => b.status === "draft");
    const unpaid = userBills.filter((b) => b.status === "unpaid");
    const overdue = userBills.filter(
      (b) => b.status !== "paid" && new Date(b.due_date) < new Date()
    );

    const sumAmount = (arr) =>
      arr.reduce((sum, b) => sum + Number(b.total_amount || 0), 0);

    return [
      { title: "Draft Bills", num: draft.length, btnText: "create new bill" },
      { title: "Total in Draft ", num: sumAmount(draft) },
      { title: "Unpaid Bills", num: unpaid.length },
      { title: "Total in Unpaid ", num: sumAmount(unpaid) },
      { title: "Overdue Bills", num: overdue.length },
      { title: "Total in Overdue", num: sumAmount(overdue) },
    ];
  }, [userBills]);

  const personalDashOtherData = useMemo(() => {
    const teamMembers = userPagination?.rowCount || 0;
    const totalContacts = filteredContactsPagenation?.count || 0;
    const totalClients = filteredContactsPagenation?.count || 0;
    const totalMatters = casePagination?.count || 0;

    return [
      { title: "Team Members", DataCout: teamMembers },
      { title: "Total Client", DataCout: totalClients },
      { title: "Total Contacts", DataCout: totalContacts },
      { title: "Total Matter", DataCout: totalMatters },
      { title: "Pending Matter", DataCout: pendingMatterCount },
    ];
  }, [
    userPagination,
    filteredContactsPagenation,
    casePagination,
    pendingMatterCount,
  ]);

  useEffect(() => {
    if (!token) return;
    if (!allCase)
      dispatch(getCases({ url: `${backend_api}case/register/`, token }));
    if (!allTask)
      dispatch(
        getAllTask({
          url: `${backend_api}task/task-register/`,
          token,
          teamMemberLawyerId,
        })
      );
    if (!allBilles)
      dispatch(getAllBilles({ url: `${backend_api}firm-case/bill/`, token }));
    if (!allActivities)
      dispatch(
        getActivities({
          url: `${backend_api}firm-case/expense/`,
          token,
          teamMemberLawyerId,
        })
      );
    if (!filteredContacts)
      dispatch(
        getAllFilterContacts({ url: `${backend_api}contact/retrieve/`, token })
      );
    if (!allUsers)
      dispatch(
        getAllUsers({ url: `${backend_api}team/create-employee/`, token })
      );
  }, [dispatch, token, teamMemberLawyerId]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleAddTask = () => {
    showDialog(<CreateTaskForm />, { dialogTitle: "Create New Task" });
  };

  const pageScrollStyle = {
    "&::-webkit-scrollbar": {
      width: "4px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "10px",
    },
  };

  const NoChartData = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        flexDirection: "column",
        color: "#666",
      }}
    >
      <Typography variant="h6">No Data Available</Typography>
      <Typography variant="body2">
        There's no data to display for this chart
      </Typography>
    </Box>
  );

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
  }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <Text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize={12}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </Text>
    );
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: "1rem",
            flexWrap: "wrap",
          }}
        >
          {personalDashOtherData?.map((item, i) => (
            <Box
              key={i}
              sx={{
                width: "12.5rem",
                display: "flex",
                flexDirection: "column",
                borderRadius: "0.5rem",
                p: "0.5rem",
                position: "relative",
                gap: "0.5rem",
                boxShadow: "0px 10px 15px -3px rgba(0,0,0,0.1)",
                border: "1px solid #e5e5e5",
                transition: "transform 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0px 15px 25px -5px rgba(0,0,0,0.15)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "1rem",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: "0.9rem", fontWeight: 500 }}
                >
                  {item.title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontSize: "1.1rem", fontWeight: 500 }}
                >
                  {item.DataCout}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  mt: "0.3rem",
                }}
              >
                <Button
                  size="small"
                  variant="text"
                  sx={{
                    textTransform: "capitalize",
                    fontSize: "0.7rem",
                    position: "absolute",
                    bottom: "0rem",
                    right: "0rem",
                  }}
                  startIcon={
                    <RemoveRedEyeRoundedIcon sx={{ fontSize: "0.8rem" }} />
                  }
                >
                  View All
                </Button>
              </Box>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: "flex", gap: "1rem" }}>
          {/* Left Column */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              width: "50%",
              p: "0.5rem",
            }}
          >
            {/* Tasks Due Today */}
            <Box
              sx={{
                border: "1px solid #e5e5e5",
                boxShadow: "0px 10px 15px -3px rgba(0,0,0,0.1)",
                borderRadius: "1rem",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-around",
                  alignItems: "center",
                  py: "0.5rem",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: "1.1rem", fontWeight: 500 }} // Smaller font
                >
                  {tasksToday.length}
                </Typography>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ bgcolor: "rgba(255,255,255,0.5)" }}
                />
                <Box>
                  <Typography
                    variant="body1"
                    sx={{ fontSize: "1.1rem", fontWeight: 500 }} // Smaller font
                  >
                    Tasks Due Today
                  </Typography>
                </Box>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ bgcolor: "rgba(255,255,255,0.5)" }}
                />
                <Tooltip title="Add new task">
                  <IconButton
                    color="inherit"
                    size="small" // Smaller button
                    onClick={handleAddTask}
                  >
                    <AddRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Divider orientation="horizontal" flexItem />
              <Box
                sx={{
                  height: "15rem",
                  p: "0.5rem", // Smaller padding
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "0.1rem",
                  overflow: "auto",
                  ...pageScrollStyle,
                }}
              >
                {tasksToday.length === 0 ? (
                  <NoDataOptionField
                    imgSrc={noOption}
                    text="Today no task assigned"
                    btnText="Task"
                    handleClickNoData={handleAddTask}
                    endIcon={<AddRoundedIcon fontSize="small" />}
                  />
                ) : (
                  tasksToday.map((item, i) => (
                    <Box key={i} sx={{ width: "100%" }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: "0.5rem",
                          p: "0.3rem", // Smaller padding
                          borderRadius: "0.5rem",
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                          },
                        }}
                      >
                        <FormControlLabel
                          control={<Checkbox size="small" />} // Smaller checkbox
                          label={
                            <Typography
                              noWrap
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                width: "7rem",
                                fontSize: "0.8rem", // Smaller font
                              }}
                            >
                              {item.task_name}
                            </Typography>
                          }
                          value="task_checkbox"
                        />
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: "0.7rem", // Smaller font
                            borderRadius: "1rem",
                            px: "0.4rem", // Smaller padding
                            py: "0.05rem",
                            color: "#fff",
                            bgcolor:
                              item.priority === "high"
                                ? "#D32F2F"
                                : item.priority === "low"
                                ? "#FFC107"
                                : "#004242",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                          }}
                        >
                          {item.priority}
                        </Typography>
                        <Box sx={{ display: "flex", gap: "0.2rem" }}>
                          <Typography
                            variant="body1"
                            sx={{ fontSize: "0.7rem" }} // Smaller font
                          >
                            {item.due_date}
                          </Typography>
                        </Box>
                      </Box>
                      <Divider orientation="horizontal" flexItem />
                    </Box>
                  ))
                )}
                <Box
                  sx={{
                    position: "absolute",
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    left: "0rem",
                    bottom: "0rem",
                    width: "100%",
                    height: "2.5rem", // Smaller height
                    background: "rgba(255, 255, 255, 0.5)",
                    backdropFilter: "blur(4px)",
                    webkitBackdropFilter: "blur(4px)",
                    pr: "1rem",
                    borderRadius: "0.5rem",
                  }}
                >
                  <Button
                    variant="text"
                    size="small" // Smaller button
                    sx={{
                      fontSize: "0.8rem",
                      textTransform: "capitalize",
                    }}
                    startIcon={<RemoveRedEyeRoundedIcon fontSize="small" />} // Smaller icon
                  >
                    View All
                  </Button>
                </Box>
              </Box>
            </Box>
            {/* Billing Metrics */}
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
            >
              <Typography
                variant="body1"
                sx={{ fontSize: "1.1rem", fontWeight: 600 }} // Smaller font
              >
                Billing Metrics
              </Typography>
              <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={1}>
                  {" "}
                  {/* Smaller spacing */}
                  {personalDashBillData.map((item, i) => (
                    <Grid key={i} item xs={6} md={6}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.5rem", // Reduced gap
                          boxShadow: "0px 10px 15px -3px rgba(0,0,0,0.1)",
                          borderRadius: "0.5rem",
                          p: "0.4rem", // Smaller padding
                          border: "1px solid #e5e5e5",
                          transition: "transform 0.2s ease-in-out",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0px 15px 25px -5px rgba(0,0,0,0.15)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{ fontSize: "0.9rem", fontWeight: 500 }} // Smaller font
                          >
                            {item.title}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontSize: "0.9rem", fontWeight: 500 }} // Smaller font
                          >
                            {item.num}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: Object.hasOwn(item, "btnText")
                              ? "space-between"
                              : "flex-end",
                          }}
                        >
                          {Object.hasOwn(item, "btnText") ? (
                            <Button
                              variant="contained"
                              size="small"
                              sx={{
                                textTransform: "capitalize",
                                fontSize: "0.6rem", // Smaller font
                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                backgroundColor: PRIMARY_COLOR,
                                "&:hover": { backgroundColor: PRIMARY_DARK },
                              }}
                            >
                              Create new bill
                            </Button>
                          ) : null}
                          <Button
                            size="small"
                            variant="text"
                            sx={{
                              textTransform: "capitalize",
                              fontSize: "0.65rem", // Smaller font
                            }}
                            startIcon={
                              <RemoveRedEyeRoundedIcon fontSize="small" />
                            } // Smaller icon
                          >
                            View All
                          </Button>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
            {/* Time Distribution */}
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
            >
              <Typography
                variant="body1"
                sx={{ fontSize: "1.1rem", fontWeight: 600 }} // Smaller font
              >
                Time Distribution
              </Typography>
              <Box
                sx={{
                  border: "1px solid #e5e5e5",
                  p: "0.5rem",
                  borderRadius: "1rem",
                  boxShadow: "0px 10px 15px -3px rgba(0,0,0,0.1)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  height: "300px",
                }}
              >
                {pieChartData.reduce((sum, item) => sum + item.value, 0) ===
                0 ? (
                  <NoChartData />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={renderCustomizedLabel}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.color}
                            stroke="#fff"
                            strokeWidth={1}
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        formatter={(value) => [`${value.toFixed(2)} hours`, ""]}
                        contentStyle={{
                          borderRadius: "8px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          fontSize: "0.8rem", // Smaller tooltip
                        }}
                      />
                      <Legend
                        layout="horizontal"
                        verticalAlign="bottom"
                        wrapperStyle={{ fontSize: "0.7rem" }} // Smaller legend
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </Box>
          </Box>

          {/* Right Column */}
          <Box
            sx={{
              width: "50%",
              p: "0.5rem",
              display: "flex",
              gap: "1rem",
              flexDirection: "column",
            }}
          >
            {/* Financial Metrics Chart */}
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
            >
              <Typography
                variant="body1"
                sx={{ fontSize: "1.1rem", fontWeight: 600 }}
              >
                Financial Metrics
              </Typography>
              <Box
                sx={{
                  border: "1px solid #e5e5e5",
                  p: "0.5rem",
                  borderRadius: "1rem",
                  boxShadow: "0px 10px 15px -3px rgba(0,0,0,0.1)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  height: "300px",
                }}
              >
                {monthlyProgressData.reduce(
                  (sum, item) => sum + item.revenue,
                  0
                ) === 0 ? (
                  <NoChartData />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={monthlyProgressData}
                      margin={{ top: 10, right: 20, left: 10, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: "0.7rem" }} />
                      <YAxis tick={{ fontSize: "0.7rem" }} />
                      <RechartsTooltip
                        formatter={(value) => [
                          `$${value.toFixed(2)}`,
                          "Revenue",
                        ]}
                        contentStyle={{
                          borderRadius: "8px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          fontSize: "0.8rem",
                        }}
                      />
                      <Bar
                        dataKey="revenue"
                        name="Revenue"
                        fill={PRIMARY_COLOR}
                        radius={[4, 4, 0, 0]}
                        barSize={25} // Smaller bars
                      >
                        {monthlyProgressData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={PRIMARY_COLOR}
                            stroke={PRIMARY_DARK}
                            strokeWidth={1}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </Box>
            {/* Hourly Metrics */}
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
            >
              <Typography
                variant="body1"
                sx={{ fontSize: "1.1rem", fontWeight: 600 }} // Smaller font
              >
                Billable Hours Target
              </Typography>
              <Box
                sx={{
                  border: "1px solid #e5e5e5",
                  p: "0.5rem",
                  borderRadius: "1rem",
                  boxShadow: "0px 10px 15px -3px rgba(0,0,0,0.1)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <TabContext value={value}>
                  <Box>
                    <TabList
                      onChange={handleChange}
                      variant="scrollable"
                      scrollButtons
                      allowScrollButtonsMobile
                      sx={{
                        minHeight: "1rem",
                        "& .MuiTab-root": {
                          fontSize: "0.7rem", // Smaller tabs
                          minHeight: "1.5rem",
                        },
                        "& .Mui-selected": {
                          color: PRIMARY_COLOR,
                        },
                        "& .MuiTabs-indicator": {
                          backgroundColor: PRIMARY_COLOR,
                        },
                      }}
                    >
                      {hourlyTabs.map((item, i) => (
                        <Tab
                          key={i}
                          label={item.period}
                          value={item.value}
                          sx={{
                            textTransform: "capitalize",
                            padding: "0.2rem 0.5rem", // Smaller padding
                          }}
                        />
                      ))}
                    </TabList>
                  </Box>

                  {hourlyTabs.map((item, i) => (
                    <TabPanel
                      key={i}
                      sx={{ p: "0.5rem" }} // Smaller padding
                      value={item.value}
                    >
                      <CircularProgress
                        percentage={item.data.percentage}
                        completed={item.data.completed}
                        target={item.data.target}
                        period={item.period}
                      />
                    </TabPanel>
                  ))}
                </TabContext>
                <Box>
                  <Button
                    variant="text"
                    size="small" // Smaller button
                    sx={{
                      textTransform: "capitalize",
                      fontSize: "0.8rem", // Smaller font
                      textDecoration: "underline",
                      color: PRIMARY_COLOR,
                    }}
                    startIcon={<SettingsRoundedIcon fontSize="small" />} // Smaller icon
                  >
                    Personal performance settings
                  </Button>
                </Box>
              </Box>
            </Box>
            {/* Billable Hours Trend */}
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontSize: "1.1rem", fontWeight: 600 }} // Smaller font
                >
                  Billable Hours Trend
                </Typography>
                <Tooltip title="Full Screen">
                  <IconButton
                    size="small" // Smaller button
                    onClick={() => setGraphFullScreen(true)}
                  >
                    <FullscreenRoundedIcon sx={{ fontSize: "1rem" }} />{" "}
                    {/* Smaller icon */}
                  </IconButton>
                </Tooltip>
              </Box>

              <AnimatePresence mode="wait">
                {!graphFullScreen ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    layoutId="chart-container"
                  >
                    <Box
                      sx={{
                        border: "1px solid #e5e5e5",
                        p: "0.5rem",
                        borderRadius: "1rem",
                        boxShadow: "0px 10px 15px -3px rgba(0,0,0,0.1)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        height: "15.5rem",
                      }}
                    >
                      {monthlyProgressData.reduce(
                        (sum, item) => sum + item.billable,
                        0
                      ) === 0 ? (
                        <NoChartData />
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart
                            data={monthlyProgressData}
                            margin={{ top: 5, right: 20, left: 0, bottom: 5 }} // Smaller margins
                          >
                            <defs>
                              <linearGradient
                                id="colorBillable"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                              >
                                <stop
                                  offset="5%"
                                  stopColor={PRIMARY_COLOR}
                                  stopOpacity={0.8}
                                />
                                <stop
                                  offset="95%"
                                  stopColor={PRIMARY_COLOR}
                                  stopOpacity={0.1}
                                />
                              </linearGradient>
                              <linearGradient
                                id="colorTarget"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                              >
                                <stop
                                  offset="5%"
                                  stopColor="#ff9800"
                                  stopOpacity={0.8}
                                />
                                <stop
                                  offset="95%"
                                  stopColor="#ff9800"
                                  stopOpacity={0.1}
                                />
                              </linearGradient>
                            </defs>
                            <CartesianGrid
                              strokeDasharray="3 3"
                              vertical={false}
                            />
                            <XAxis
                              dataKey="month"
                              tick={{ fontSize: "0.7rem" }} // Smaller ticks
                            />
                            <YAxis
                              tick={{ fontSize: "0.7rem" }} // Smaller ticks
                            />
                            <RechartsTooltip
                              formatter={(value, name) => [
                                `${value.toFixed(2)} hours`,
                                name,
                              ]}
                              contentStyle={{
                                borderRadius: "8px",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                fontSize: "0.8rem", // Smaller tooltip
                              }}
                            />
                            <Area
                              type="monotone"
                              dataKey="billable"
                              stroke={PRIMARY_COLOR}
                              fillOpacity={1}
                              fill="url(#colorBillable)"
                              name="Billable Hours"
                              strokeWidth={1} // Thinner line
                            />
                            <Area
                              type="monotone"
                              dataKey="target"
                              stroke="#ff9800"
                              fillOpacity={0.1}
                              fill="url(#colorTarget)"
                              name="Target"
                              strokeDasharray="5 5"
                              strokeWidth={1} // Thinner line
                            />
                            <Legend
                              wrapperStyle={{
                                fontSize: "0.7rem", // Smaller legend
                                paddingTop: "5px",
                              }}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      )}
                    </Box>
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      width: "100vw",
                      height: "100vh",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      zIndex: 1000,
                    }}
                  >
                    <motion.div
                      layoutId="chart-container"
                      style={{
                        backgroundColor: "#fff",
                        borderRadius: "0.5rem",
                        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.3)",
                        width: "90%",
                        height: "90%",
                        position: "relative",
                        overflow: "hidden",
                      }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 30,
                        duration: 0.5,
                      }}
                    >
                      <Box sx={{ height: "100%", p: "1.2rem" }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1, // Smaller margin
                          }}
                        >
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {" "}
                            {/* Smaller heading */}
                            Billable Hours Trend
                          </Typography>
                          <IconButton
                            onClick={() => setGraphFullScreen(false)}
                            size="small" // Smaller button
                          >
                            <CloseRoundedIcon />
                          </IconButton>
                        </Box>

                        <Box
                          sx={{
                            position: "relative",
                            height: "calc(100% - 50px)", // Adjusted height
                          }}
                        >
                          {isLoading ? (
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "white",
                              }}
                            >
                              <Box className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
                            </motion.div>
                          ) : monthlyProgressData.reduce(
                              (sum, item) => sum + item.billable,
                              0
                            ) === 0 ? (
                            <NoChartData />
                          ) : (
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ duration: 0.3, delay: 0.2 }}
                              style={{ height: "100%", width: "100%" }}
                            >
                              <ResponsiveContainer width="100%" height="100%">
                                <AreaChart
                                  data={monthlyProgressData}
                                  margin={{
                                    top: 5,
                                    right: 20,
                                    left: 20,
                                    bottom: 10,
                                  }}
                                >
                                  <defs>
                                    <linearGradient
                                      id="colorBillable"
                                      x1="0"
                                      y1="0"
                                      x2="0"
                                      y2="1"
                                    >
                                      <stop
                                        offset="5%"
                                        stopColor={PRIMARY_COLOR}
                                        stopOpacity={0.8}
                                      />
                                      <stop
                                        offset="95%"
                                        stopColor={PRIMARY_COLOR}
                                        stopOpacity={0.1}
                                      />
                                    </linearGradient>
                                    <linearGradient
                                      id="colorTarget"
                                      x1="0"
                                      y1="0"
                                      x2="0"
                                      y2="1"
                                    >
                                      <stop
                                        offset="5%"
                                        stopColor="#ff9800"
                                        stopOpacity={0.8}
                                      />
                                      <stop
                                        offset="95%"
                                        stopColor="#ff9800"
                                        stopOpacity={0.1}
                                      />
                                    </linearGradient>
                                  </defs>
                                  <CartesianGrid
                                    strokeDasharray="3 3"
                                    vertical={false}
                                  />
                                  <XAxis
                                    dataKey="month"
                                    tick={{ fontSize: "0.8rem" }} // Smaller ticks
                                  />
                                  <YAxis
                                    tick={{ fontSize: "0.8rem" }} // Smaller ticks
                                  />
                                  <RechartsTooltip
                                    formatter={(value, name) => [
                                      `${value.toFixed(2)} hours`,
                                      name,
                                    ]}
                                    contentStyle={{
                                      borderRadius: "8px",
                                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                      fontSize: "0.9rem", // Smaller tooltip
                                    }}
                                  />
                                  <Area
                                    type="monotone"
                                    dataKey="billable"
                                    stroke={PRIMARY_COLOR}
                                    fillOpacity={1}
                                    fill="url(#colorBillable)"
                                    name="Billable Hours"
                                    strokeWidth={1} // Thinner line
                                  />
                                  <Area
                                    type="monotone"
                                    dataKey="target"
                                    stroke="#ff9800"
                                    fillOpacity={0.1}
                                    fill="url(#colorTarget)"
                                    name="Target"
                                    strokeDasharray="5 5"
                                    strokeWidth={1} // Thinner line
                                  />
                                  <Legend
                                    wrapperStyle={{
                                      fontSize: "0.8rem", // Smaller legend
                                      paddingTop: "10px",
                                    }}
                                  />
                                </AreaChart>
                              </ResponsiveContainer>
                            </motion.div>
                          )}
                        </Box>
                      </Box>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PersonalDash;
